const API_URL = import.meta.env['VITE_API_URL'] ?? '/api';

/**
 * Une lecture qui doit venir du serveur, pas d'un cache : tirer pour rafraîchir. `no-store`
 * passe outre le cache HTTP, et le service worker (`sw.ts`) le lit pour sauter son cache
 * NetworkFirst — sans ce marqueur, un réseau lent (> 3 s) rendrait la copie de la veille.
 */
export const FRAIS: RequestInit = { cache: 'no-store' };

const TOKEN_KEY = 'nota_token';

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function setToken(token: string): void {
  localStorage.setItem(TOKEN_KEY, token);
}

export function clearToken(): void {
  localStorage.removeItem(TOKEN_KEY);
}

/**
 * Ce que fait l'app quand le serveur refuse son jeton (expiré, révoqué) : posé par le store
 * d'auth, qui efface la session — `RequireAuth` renvoie alors vers /login.
 */
let onUnauthorized: (() => void) | null = null;

export function setUnauthorizedHandler(handler: () => void): void {
  onUnauthorized = handler;
}

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string
  ) {
    super(message);
  }
}

/**
 * Le réseau n'a pas répondu — à distinguer d'une erreur HTTP, qui reste une `ApiError`.
 * C'est le signal que les stores attrapent pour mettre l'écriture en file au lieu de
 * la perdre ([ADR 0010](../../../docs/adr/0010-ecritures-offline-file-de-rejeu.md)).
 */
export class OfflineError extends Error {
  constructor() {
    super('Hors-ligne — l’action sera envoyée au retour du réseau');
  }
}

/**
 * `navigator.onLine` ment dans le cas exact qui motive tout ceci : le wifi du métro
 * est « connecté » et ne route rien. Il ne sert donc que de raccourci pour éviter un
 * `fetch` qu'on sait condamné ; la détection qui fait autorité est le rejet du `fetch`
 * lui-même, plus bas.
 *
 * Les GET ne sont jamais court-circuités : hors-ligne, ils traversent le service
 * worker, qui les sert depuis son cache.
 */
function knownOffline(method: string): boolean {
  return method !== 'GET' && typeof navigator !== 'undefined' && navigator.onLine === false;
}

export async function api<T>(
  path: string,
  init: RequestInit & { auth?: boolean } = {}
): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...((init.headers as Record<string, string>) ?? {}),
  };
  const token = init.auth !== false ? getToken() : null;
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const method = init.method ?? 'GET';
  if (knownOffline(method)) throw new OfflineError();

  let res: Response;
  try {
    res = await fetch(`${API_URL}${path}`, { ...init, headers });
  } catch {
    // `fetch` ne rejette que sur un échec réseau (ou un CORS cassé, qui ne se
    // produit pas ici) : tout ce qui est arrivé jusqu'au serveur ressort en ApiError.
    throw new OfflineError();
  }

  if (res.status === 204) return undefined as T;

  // Seul le refus de la session encore en place la ferme : un mauvais mot de passe au login
  // (`auth: false`) ou la réponse tardive d'une session déjà remplacée n'y touchent pas. Un
  // jeton disparu du stockage (logout dans un autre onglet) compte : le store le croit encore là.
  if (res.status === 401 && init.auth !== false && token === getToken()) onUnauthorized?.();

  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = (body as { error?: { code: string; message: string } }).error;
    throw new ApiError(
      res.status,
      err?.code ?? 'UNKNOWN',
      err?.message ?? res.statusText
    );
  }
  return body as T;
}
